import {computed, Signal, signal} from '@angular/core';
import {CycleLogActionType, CycleLogDoneStatusType, ICycleLog} from '@core/api/data-access';
import dayjs from 'dayjs';

import {CycleLogEntry} from './cycle-log-entry.model'; // Assuming this is in the same directory or imported correctly

export class CycleLogsModel {
	// Keep logs as a signal; could be WritableSignal if mutated internally,
	// or just Signal if received immutably. Let's assume it's set once.
	private readonly _logs: Signal<CycleLogEntry[]>;

	// --- Computed Maps for Efficient Lookups (Computed once per log change) ---

	// Group logs by station ordinal.
	public readonly logsByStationOrdinalMap$: Signal<Map<number, CycleLogEntry[]>> = computed(() => {
		// console.log('Recomputing logsByStationOrdinalMap$ in CycleLogsModel');
		const map = new Map<number, CycleLogEntry[]>();
		this._logs().forEach(log => {
			const ordinal = log.stationOrdinal; // Assumes stationOrdinal is correctly set
			if (ordinal !== undefined && !isNaN(ordinal)) {
				// Check if ordinal is valid
				if (!map.has(ordinal)) {
					map.set(ordinal, []);
				}
				map.get(ordinal)?.push(log);
			}
			// Optionally handle logs without a valid ordinal (e.g., cycle-level logs)
		});
		return map;
	});

	// Group logs by action type.
	public readonly logsByActionMap$: Signal<Map<CycleLogActionType, CycleLogEntry[]>> = computed(() => {
		// console.log('Recomputing logsByActionMap$ in CycleLogsModel');
		const map = new Map<CycleLogActionType, CycleLogEntry[]>();
		this._logs().forEach(log => {
			const action = log.action;
			if (!map.has(action)) {
				map.set(action, []);
			}
			map.get(action)?.push(log);
		});
		return map;
	});

	// Group logs by user ID.
	public readonly logsByUserMap$: Signal<Map<string, CycleLogEntry[]>> = computed(() => {
		// console.log('Recomputing logsByUserMap$ in CycleLogsModel');
		const map = new Map<string, CycleLogEntry[]>();
		this._logs().forEach(log => {
			const user = log.user;
			if (user) {
				// Ensure user ID exists
				if (!map.has(user)) {
					map.set(user, []);
				}
				map.get(user)?.push(log);
			}
		});
		return map;
	});

	// --- Computed Signals for Specific Log Queries ---

	// Logs sorted by timestamp (ascending).
	public readonly sortedLogs$: Signal<CycleLogEntry[]> = computed(() => {
		// console.log('Recomputing sortedLogs$ in CycleLogsModel');
		// Create a new sorted array to avoid mutating the original signal's array
		return [...this._logs()].sort((a, b) => dayjs(a.timestamp).diff(dayjs(b.timestamp)));
	});

	// The log entry indicating the last user approval action.
	public readonly userSigned$: Signal<CycleLogEntry | null> = computed(() => {
		// console.log('Recomputing userSigned$ in CycleLogsModel');
		// Search from the end of the sorted logs for efficiency
		return (
			this.sortedLogs$()
				.slice() // Create copy before reversing
				.reverse()
				.find(log => log.action === CycleLogActionType.UserApproved) ?? null
		);
	});

	// The first log entry indicating a user finished interaction (Approved, Rejected, Alert, Timedout).
	// Note: 'find' gets the *first* match in sorted order (earliest). If latest is needed, reverse first.
	public readonly userFinishCycle$: Signal<CycleLogEntry | null> = computed(() => {
		// console.log('Recomputing userFinishCycle$ in CycleLogsModel');
		const finishActions = [CycleLogActionType.UserApproved, CycleLogActionType.UserRejected, CycleLogActionType.UserAlert, CycleLogActionType.UserTimedout];
		// Find the *first* such log chronologically
		return this.sortedLogs$().find(log => finishActions.includes(log.action)) ?? null;
	});

	// The last log entry indicating a significant user status update.
	public readonly lastUserUpdate$: Signal<CycleLogEntry | null> = computed(() => {
		// console.log('Recomputing lastUserUpdate$ in CycleLogsModel');
		const updateActions = [
			CycleLogActionType.UserApproved,
			CycleLogActionType.UserRejected,
			CycleLogActionType.UserNotified,
			CycleLogActionType.UserAlert,
			// Add others like UserArrived, UserDeparted if they count as updates
		];
		// Find the *latest* such log chronologically
		return (
			this.sortedLogs$()
				.slice() // Create copy
				.reverse() // Search from the end
				.find(log => updateActions.includes(log.action)) ?? null
		);
	});

	// Check if any station alert log exists.
	public readonly cycleAlerted$: Signal<boolean> = computed(() => {
		// console.log('Recomputing cycleAlerted$ in CycleLogsModel');
		return (this.logsByActionMap$().get(CycleLogActionType.StationAlert)?.length ?? 0) > 0;
	});

	// Check if there are any logs with comments (in description) associated with specific actions.
	public readonly cycleHasComments$: Signal<boolean> = computed(() => {
		// console.log('Recomputing cycleHasComments$ in CycleLogsModel');
		const commentActions = [
			CycleLogActionType.UserAlert,
			CycleLogActionType.UserRejected,
			CycleLogActionType.UserApproved, // Sometimes approval might have optional comments
			CycleLogActionType.UserCommented, // Explicit comments
		];
		// Check if any log matches the actions *and* has a non-empty description
		return this._logs().some(log => commentActions.includes(log.action) && !!log.description);
	});

	// Get the description from the last CycleStarted log, if valid.
	public readonly openingMessage$: Signal<string> = computed(() => {
		// console.log('Recomputing openingMessage$ in CycleLogsModel');
		// Find the latest 'CycleStarted' log
		const logMessage = this.logsByActionMap$()
			?.get(CycleLogActionType.CycleStarted)
			?.slice() // copy
			?.reverse()?.[0]?.description; // get last one

		// Basic validation for the message content
		return logMessage && logMessage !== 'undefined' && !logMessage.startsWith('{\\"type\\":\\') ? logMessage : '';
	});

	// Get the description of the last log indicating cycle creation or update.
	public readonly lastEditOpeningMessage$: Signal<string> = computed(() => {
		// console.log('Recomputing lastEditOpeningMessage$ in CycleLogsModel');
		const editActions = [CycleLogActionType.CycleCreated, CycleLogActionType.CycleUpdated, CycleLogActionType.CycleTemplatePrivate, CycleLogActionType.CycleTemplatePublic];
		// Find the latest log matching these actions
		return (
			this.sortedLogs$()
				.slice() // copy
				.reverse() // search from end
				.find(log => editActions.some(l => l === log.action))?.description ?? ''
		); // Return description or empty string
	});

	// Get the very last log entry in chronological order.
	public readonly finishedTypeLog$: Signal<CycleLogEntry | undefined> = computed(() => {
		// console.log('Recomputing finishedTypeLog$ in CycleLogsModel');
		const sorted = this.sortedLogs$();
		return sorted[sorted.length - 1]; // Returns undefined if array is empty
	});

	// Check if the cycle is a draft that has been edited (has CycleUpdated log).
	public readonly isEditedDraftCycle$: Signal<boolean> = computed(() => {
		// console.log('Recomputing isEditedDraftCycle$ in CycleLogsModel');
		// More efficient check using the map
		return (this.logsByActionMap$().get(CycleLogActionType.CycleUpdated)?.length ?? 0) > 0;
		// Or using _logs() directly:
		// return this._logs().some(log => log.action === CycleLogActionType.CycleUpdated);
	});

	// Constructor: Processes raw logs and sets the internal signal.
	constructor(rawLogs: ICycleLog[] | undefined) {
		const logs = rawLogs ?? [];
		const logEntries: CycleLogEntry[] = [];
		let currentOrdinal: number | undefined = undefined; // Use undefined for cycle-level logs initially

		logs.forEach(log => {
			const logEntry = new CycleLogEntry(log); // Create base entry

			// Determine station ordinal based on actions
			if (logEntry.action === CycleLogActionType.StationArrived) {
				// Station arrival log's description *is* the ordinal
				const parsedOrdinal = parseInt(logEntry.description, 10);
				if (!isNaN(parsedOrdinal)) {
					currentOrdinal = parsedOrdinal;
				} else {
					// Handle cases where description might not be a number for StationArrived
					console.warn(`StationArrived log has non-numeric description: ${logEntry.description}`, log);
					// Keep previous ordinal or reset? Depends on desired behavior.
					// currentOrdinal = undefined; // Or keep the last known valid ordinal
				}
			} else if (Object.values(CycleLogDoneStatusType).some(doneAction => doneAction == logEntry.action)) {
				// Cycle completion logs reset the station context
				currentOrdinal = undefined; // Or Infinity as originally, if that has specific meaning later
			} else if (logEntry.action === CycleLogActionType.CycleStarted || logEntry.action === CycleLogActionType.CycleCreated) {
				// Cycle start/create logs are not associated with a station ordinal
				currentOrdinal = undefined;
			}
			// Station-specific logs without an explicit StationArrived beforehand might inherit the currentOrdinal
			// or should be treated as cycle-level? Current logic assigns the last known ordinal.

			logEntry.stationOrdinal = currentOrdinal;
			logEntries.push(logEntry);
		});

		// Set the signal with the processed entries
		this._logs = signal(logEntries, {equal: (a, b) => a === b}); // Use signal() directly
	}

	/**
	 * Public method to get logs filtered by station ordinal.
	 * @param ordinal The station ordinal number.
	 * @returns An array of CycleLogEntry for the given station.
	 */
	public getLogsByStationOrdinal(ordinal: number): CycleLogEntry[] {
		return this.logsByStationOrdinalMap$().get(ordinal) ?? [];
	}

	/**
	 * Public method to get logs filtered by user ID.
	 * @param userId The user's ID.
	 * @returns An array of CycleLogEntry for the given user.
	 */
	public getLogsByUser(userId: string): CycleLogEntry[] {
		return this.logsByUserMap$().get(userId) ?? [];
	}

	/**
	 * Generic method to retrieve logs by action(s), optionally getting first/last.
	 * Tries to use the computed map for efficiency for single actions.
	 * @param action Single action type or array of action types.
	 * @param options Specify { first: true } or { last: true }.
	 * @returns An array of logs, or a single log if first/last is true.
	 */
	public getLogsByAction<T extends CycleLogEntry[] | CycleLogEntry | undefined>(action: CycleLogActionType | CycleLogActionType[], options: {first?: boolean; last?: boolean} = {}): T {
		let filteredLogs: CycleLogEntry[] = [];

		if (!Array.isArray(action)) {
			// Optimize for single action using the map
			filteredLogs = this.logsByActionMap$().get(action) ?? [];
		} else {
			// Filter manually for multiple actions
			filteredLogs = this._logs().filter(l => action.includes(l.action));
		}

		// Sort only if needed (first/last requested) or if results came from manual filter
		const requiresSort = options.first || options.last || Array.isArray(action);
		const sortedLogs = requiresSort ? [...filteredLogs].sort((a, b) => dayjs(a.timestamp).diff(dayjs(b.timestamp))) : filteredLogs;

		if (options.first) {
			return sortedLogs[0] as T; // Returns undefined if array is empty
		}
		if (options.last) {
			return sortedLogs[sortedLogs.length - 1] as T; // Returns undefined if array is empty
		}
		return sortedLogs as T;
	}

	// Expose the raw logs signal if needed directly
	public get logsSignal(): Signal<CycleLogEntry[]> {
		return this._logs;
	}
}
